// Org chart person slide-over: role, teams (with lead marks), reports-to and out-of-office.
import { useState } from "react";
import { toast } from "sonner";
import { Palmtree, Save, X, Crown } from "lucide-react";
import { workspaceApi, teamColor } from "../_shared";

export default function OrgPersonPopover({ node, teams, managers, isEditor, onClose, onSaved }) {
  const [oooReason, setOooReason] = useState(node?.out_of_office?.reason || "");
  const [oooUntil, setOooUntil] = useState(node?.out_of_office?.until || "");
  const [reportsTo, setReportsTo] = useState(node?.reports_to || "");
  const [saving, setSaving] = useState(false);
  if (!node) return null;

  const save = async () => {
    setSaving(true);
    try {
      await workspaceApi.patchOrgChart({
        out_of_office: { [node.id]: oooReason ? { reason: oooReason, until: oooUntil || null } : null },
        reports_to: { [node.id]: reportsTo || null },
      });
      toast.success("Saved"); onSaved?.(); onClose?.();
    } catch (e) { toast.error("Save failed", { description: e?.response?.data?.detail || "" }); }
    finally { setSaving(false); }
  };
  const memberOf = teams.filter((t) => (t.member_ids || []).includes(node.id));
  const H = ({ children }) => <div className="text-[10px] font-mono text-slate-500 uppercase tracking-widest mb-1">{children}</div>;

  return (
    <div className="fixed inset-0 z-50 flex" data-testid="ws-org-person-popover">
      <div className="flex-1 bg-black/60" onClick={onClose} />
      <div className="w-full max-w-sm bg-[#0F1420] border-l border-white/10 p-4 space-y-4 overflow-y-auto">
        <div className="flex items-start justify-between">
          <div>
            <H>Person</H>
            <div className="text-lg font-semibold text-white">{node.name}</div>
            <div className="text-[11px] text-slate-400">{node.email}</div>
          </div>
          <button onClick={onClose} className="p-1 text-slate-400 hover:text-white" data-testid="ws-org-popover-close"><X className="w-4 h-4" /></button>
        </div>
        <div><H>Role</H><div className="text-[12.5px] text-slate-200">{node.job_title || node.role_workspace || node.role || <span className="text-slate-500">—</span>}</div></div>
        <div>
          <H>Teams · {memberOf.length}</H>
          {memberOf.length === 0 && <div className="text-[11.5px] text-slate-500 italic">Not on any team.</div>}
          <div className="flex flex-wrap gap-1.5">
            {memberOf.map((t) => { const c = teamColor(t); return (
              <span key={t.id} className="text-[11px] px-2 py-0.5 rounded-full border inline-flex items-center gap-1" style={{ borderColor: `${c}66`, background: `${c}1a`, color: c }}>
                {t.name}{(t.manager_ids || []).includes(node.id) && <Crown className="w-3 h-3 text-amber-300" />}
              </span>); })}
          </div>
        </div>
        <div>
          <H>Reports to</H>
          <select className="ws-input" value={reportsTo} onChange={(e) => setReportsTo(e.target.value)} disabled={!isEditor} data-testid="ws-org-popover-reports">
            <option value="">— nobody —</option>
            {managers.filter((m) => m.id !== node.id).map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
          </select>
        </div>
        <div className="space-y-2">
          <div className="text-[10px] font-mono text-slate-500 uppercase tracking-widest flex items-center gap-1.5"><Palmtree className="w-3 h-3 text-rose-300" /> Out of office</div>
          <input className="ws-input" placeholder="Reason (e.g. Sick, Holiday) — empty to clear" value={oooReason} onChange={(e) => setOooReason(e.target.value)} disabled={!isEditor} data-testid="ws-org-popover-ooo-reason" />
          <input type="date" className="ws-input" value={oooUntil} onChange={(e) => setOooUntil(e.target.value)} disabled={!isEditor} data-testid="ws-org-popover-ooo-until" />
        </div>
        {isEditor && (
          <div className="flex items-center gap-2 pt-2 border-t border-white/5">
            <button onClick={save} disabled={saving} className="px-3 py-1.5 rounded border border-amber-500/40 bg-amber-500/10 text-amber-200 font-mono uppercase tracking-widest text-[10.5px] hover:bg-amber-500/20 flex items-center gap-1.5" data-testid="ws-org-popover-save"><Save className="w-3 h-3" /> Save</button>
            <button onClick={onClose} className="text-[11px] font-mono text-slate-400 hover:text-white">Cancel</button>
          </div>
        )}
      </div>
    </div>
  );
}
